import { useEffect, useMemo, useState } from "react";

import { ClipboardList, Search } from "lucide-react";

import {
  SearchModal,
  highlightMatch,
  matchSnippet,
  type SearchModalItem,
} from "@/components/cinch/SearchModal";

import type { Workspace } from "../api";
import { BOARD_META, type Board } from "../constants";

type TabKey = Board | "swot_summary" | "summary";

/**
 * Session-wide search: every card (SWOT walls, Impact vs Effort grid, your own
 * drafts), every lane, and every AI lane summary. Results carry the icon of
 * the tab they live under; selecting jumps there, scrolls the exact card into
 * frame, and shakes/halos it until the next click. Ctrl/Cmd+K opens it.
 */
export function SessionSearch({
  workspace,
  onNavigate,
}: {
  workspace: Workspace;
  /** `highlightKey` matches a `data-planning-hl` attribute on the target. */
  onNavigate: (tab: TabKey, highlightKey?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const items = useMemo<SearchModalItem[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const laneName = (id: string | null) =>
      id ? workspace.categories.find((c) => c.id === id)?.name : undefined;

    const go = (tab: TabKey, highlightKey?: string) => () => {
      setOpen(false);
      setQuery("");
      onNavigate(tab, highlightKey);
    };

    const hits: { rank: number; item: SearchModalItem }[] = [];

    // Cards: SWOT + grid notes (posted everywhere, drafts only ever ours —
    // the workspace payload already enforces draft privacy).
    for (const n of workspace.notes) {
      const inTitle = n.title.toLowerCase().includes(q);
      const inDesc = (n.description ?? "").toLowerCase().includes(q);
      if (!inTitle && !inDesc) continue;
      const meta = BOARD_META[n.board];
      hits.push({
        rank: inTitle ? 0 : 1,
        item: {
          id: `note-${n.id}`,
          icon: meta.icon,
          title: highlightMatch(n.title, query),
          snippet: n.description
            ? highlightMatch(matchSnippet(n.description, query), query)
            : undefined,
          hint:
            n.status === "draft"
              ? "My draft"
              : (laneName(n.category_id) ?? meta.label),
          onSelect: go(n.board, `note-${n.id}`),
        },
      });
    }

    // Lanes by name → their board tab.
    for (const c of workspace.categories) {
      if (!c.name.toLowerCase().includes(q)) continue;
      const meta = BOARD_META[c.board];
      hits.push({
        rank: 0,
        item: {
          id: `lane-${c.id}`,
          icon: meta.icon,
          title: highlightMatch(c.name, query),
          snippet: `Lane on ${meta.label}`,
          hint: "Lane",
          onSelect: go(c.board, `lane-${c.id}`),
        },
      });
    }

    // AI lane summaries → the SWOT Summary tab, where they're read.
    for (const c of workspace.categories) {
      if (!c.summary || !c.summary.toLowerCase().includes(q)) continue;
      hits.push({
        rank: 2,
        item: {
          id: `summary-${c.id}`,
          icon: ClipboardList,
          title: `${c.name} — AI summary`,
          snippet: highlightMatch(matchSnippet(c.summary, query), query),
          hint: "Summary",
          onSelect: go("swot_summary", `lanesum-${c.id}`),
        },
      });
    }

    return hits
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 30)
      .map((h) => h.item);
  }, [workspace, query, onNavigate]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ml-auto inline-flex h-7 items-center gap-1.5 rounded-md border bg-background px-2.5 text-xs text-muted-foreground transition-colors hover:border-brand/50 hover:text-foreground"
      >
        <Search className="h-3.5 w-3.5" />
        Search session
        <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Ctrl K</kbd>
      </button>
      <SearchModal
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQuery("");
        }}
        query={query}
        onQueryChange={setQuery}
        items={items}
        placeholder="Search notes, lanes, and summaries…"
        emptyText="Nothing in this session matches."
        idleText="Search every card, lane, and AI summary in this session."
      />
    </>
  );
}
